<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import {
  isFirstVisit,
  isFirstPurchase,
  isInvestor,
  normalizePaymentType,
  priceRangeLabel,
  proposalsInProgressQuantity,
  proposalsQuantity,
  shouldRenderOfferSection,
  shouldRenderPaymentTypeSection,
  shouldRenderPriceRange,
  visitorName,
  visitorPaymentType,
  visitsQuantity,
} from '../utils/visitorInfoPresenter';

const props = defineProps({
  visitorInfo: { type: Object, default: null },
  businessContext: { type: String, default: 'for_rent' },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const name = computed(
  () => visitorName(props.visitorInfo) || t('LEADS.VISITOR_INFO.ANONYMOUS')
);
const visitCount = computed(() => visitsQuantity(props.visitorInfo));
const ongoingOffers = computed(() =>
  proposalsInProgressQuantity(props.visitorInfo)
);
const totalOffers = computed(() => proposalsQuantity(props.visitorInfo));
const paymentType = computed(() => visitorPaymentType(props.visitorInfo));
const normalizedPaymentType = computed(() =>
  normalizePaymentType(paymentType.value)
);
const paymentTitle = computed(() => {
  if (normalizedPaymentType.value === 'cash') {
    return t('LEADS.VISITOR_INFO.INFO.PAYMENT_CASH');
  }
  if (normalizedPaymentType.value === 'financed') {
    return t('LEADS.VISITOR_INFO.INFO.PAYMENT_FINANCED');
  }
  return t('LEADS.VISITOR_INFO.INFO.PAYMENT_OTHER');
});
const firstVisitDescription = computed(() =>
  props.businessContext === 'for_sale'
    ? t('LEADS.VISITOR_INFO.INFO.FIRST_PURCHASE_VISIT')
    : t('LEADS.VISITOR_INFO.INFO.FIRST_RENT_VISIT')
);
const visitedTitle = computed(() =>
  visitCount.value > 0
    ? t('LEADS.VISITOR_INFO.INFO.VISITED_TITLE', {
        count: visitCount.value,
      })
    : t('LEADS.VISITOR_INFO.INFO.NO_VISITS_TITLE')
);
const visitedDescription = computed(() =>
  visitCount.value > 0
    ? t('LEADS.VISITOR_INFO.INFO.VISITED_DESCRIPTION')
    : firstVisitDescription.value
);
const offersTitle = computed(() =>
  ongoingOffers.value > 0
    ? t('LEADS.VISITOR_INFO.INFO.ONGOING_OFFERS_TITLE', {
        count: ongoingOffers.value,
      })
    : t('LEADS.VISITOR_INFO.INFO.NO_ONGOING_OFFERS')
);
const offersDescription = computed(() =>
  totalOffers.value > 0
    ? t('LEADS.VISITOR_INFO.INFO.ONGOING_OFFERS_DESCRIPTION', {
        count: totalOffers.value,
      })
    : t('LEADS.VISITOR_INFO.INFO.NO_OFFERS_DESCRIPTION')
);
const items = computed(() => {
  const rows = [];

  if (shouldRenderPriceRange(props.visitorInfo)) {
    rows.push({
      key: 'price',
      icon: 'i-lucide-building-2',
      title: priceRangeLabel(props.visitorInfo),
      description: t('LEADS.VISITOR_INFO.INFO.PRICE_RANGE_DESCRIPTION'),
    });
  }

  rows.push({
    key: 'visits',
    icon: 'i-lucide-calendar-days',
    title: visitedTitle.value,
    description: visitedDescription.value,
  });

  if (shouldRenderOfferSection(props.businessContext)) {
    rows.push({
      key: 'offers',
      icon: 'i-lucide-message-square-text',
      title: offersTitle.value,
      description: offersDescription.value,
    });
  }

  if (
    shouldRenderPaymentTypeSection(props.businessContext, paymentType.value)
  ) {
    rows.push({
      key: 'payment',
      icon: 'i-lucide-banknote',
      title: paymentTitle.value,
      description: t('LEADS.VISITOR_INFO.INFO.PAYMENT_DESCRIPTION'),
    });
  }

  return rows;
});
</script>

<template>
  <LaisCard density="tight">
    <div v-if="loading" class="grid gap-3">
      <span class="h-5 w-56 animate-pulse rounded bg-lais-bg-muted" />
      <span class="h-4 w-40 animate-pulse rounded bg-lais-bg-muted" />
      <span class="h-16 w-full animate-pulse rounded bg-lais-bg-muted" />
    </div>
    <div v-else class="grid gap-4">
      <div class="min-w-0">
        <h2
          class="truncate font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
        >
          {{ name }}
        </h2>
        <div class="mt-2 flex flex-wrap gap-2">
          <StatusTag
            v-if="isFirstVisit(visitorInfo)"
            :label="t('LEADS.VISITOR_INFO.TAGS.FIRST_VISIT')"
            variant="success"
            size="xs"
          />
          <StatusTag
            v-if="isFirstPurchase(visitorInfo)"
            :label="t('LEADS.VISITOR_INFO.TAGS.FIRST_PURCHASE')"
            variant="attention"
            size="xs"
          />
          <StatusTag
            v-if="isInvestor(visitorInfo)"
            :label="t('LEADS.VISITOR_INFO.TAGS.INVESTOR')"
            variant="attention"
            size="xs"
          />
        </div>
      </div>

      <div class="grid gap-3">
        <div
          v-for="item in items"
          :key="item.key"
          class="flex items-start gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3"
        >
          <span
            class="mt-0.5 size-4 shrink-0 text-lais-primary"
            :class="item.icon"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="font-semibold text-lais-text">
              {{ item.title }}
            </p>
            <p class="mt-0.5 text-lais-body-sm text-lais-text-muted">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </LaisCard>
</template>
